import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Heart, Users, Camera, Gift, Baby, Clock } from 'lucide-react';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/4.jpg`;

export function ProtocolSection() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const enterStart = vh * 0.7;
      const enterEnd = -vh * 0.15;
      const progress = Math.max(
        0,
        Math.min(1, (enterStart - rect.top) / (enterStart - enterEnd))
      );
      setScrollProgress(progress);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const protocols = [
    { icon: Heart, title: 'Recepción para adultos', description: 'Apreciamos mucho a los pequeños, pero esta celebración será solo para adultos.' },
    { icon: Clock, title: 'Puntualidad', description: 'La ceremonia comenzará a las 17:00 hrs en punto. Te pedimos llegar 15 minutos antes.' },
    //{ icon: Camera, title: 'Desconexión Digital', description: 'Durante la ceremonia, te invitamos a vivir el momento. Nuestro fotógrafo capturará cada instante.' },
    { icon: Users, title: 'Confirmación', description: 'Por favor confirma tu asistencia antes del 31 de marzo de 2026.' },
    { icon: Gift, title: 'Mesa de Regalos', description: 'Tu presencia es nuestro mayor regalo, pero si deseas obsequiarnos algo, encontrarás nuestras opciones más adelante.' },
  ];

  return (
    <section
      ref={sectionRef}
      className="snap-section relative min-h-screen py-16 md:py-24 overflow-visible flex items-center justify-center"
    >
      <div className="section-content w-full px-6 flex flex-col justify-center">
        <div
          className="section-bg-image absolute inset-0 z-0"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
        <div className="relative z-10 w-full max-w-5xl mx-auto">
          <div
            className="text-center mb-16 transition-all duration-700 ease-out"
            style={{
              opacity: Math.max(0, Math.min(1, scrollProgress / 0.4)),
              transform: `translateY(${24 * (1 - Math.min(1, scrollProgress / 0.4))}px)`,
            }}
          >
            <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
              CONSIDERACIONES
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-4"
              style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
            >
              Para que todos disfrutemos al máximo, te compartimos algunos detalles importantes
            </p>
            <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protocols.map((protocol, index) => {
              const Icon = protocol.icon;
              const stagger = index * 0.06;
              const cardProgress = Math.max(0, Math.min(1, (scrollProgress - stagger) / 0.35));
              const opacity = cardProgress;
              const translateY = 28 * (1 - cardProgress);
              return (
                <div
                  key={protocol.title}
                  className="rounded-xl p-6 border bg-[var(--butter)]/50 backdrop-blur-sm min-h-0 overflow-visible transition-all duration-700 ease-out"
                  style={{
                    borderColor: 'rgba(186, 159, 56, 0.25)',
                    opacity,
                    transform: `translateY(${translateY}px)`,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4 border shrink-0"
                    style={{
                      backgroundColor: 'var(--pale-lime)',
                      borderColor: 'var(--chartreuse)',
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: 'var(--emerald)' }} />
                  </div>
                  <h3
                    className="text-editorial-title text-base tracking-[0.12em] mb-3"
                    style={{ fontFamily: 'var(--font-serif)' }}
                  >
                    {protocol.title.toUpperCase()}
                  </h3>
                  <p
                    className="text-sm leading-relaxed break-words overflow-visible"
                    style={{ color: 'var(--emerald)', opacity: 0.8 }}
                  >
                    {protocol.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
